<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  panelCount: number;
  ariaLabel: string;
  testId?: string;
}>();

const scroller = ref<HTMLElement | null>(null);
const currentPanel = ref(0);

function clampPanel(panel: number): number {
  const lastPanel = Math.max(props.panelCount - 1, 0);
  return Math.min(Math.max(panel, 0), lastPanel);
}

function getPanelElements(): HTMLElement[] {
  return Array.from(
    scroller.value?.querySelectorAll<HTMLElement>('.panel-scroller__panel') ??
      []
  );
}

function getPanelLeft(panel: number): number {
  const element = scroller.value;
  const panelElement = getPanelElements()[panel];

  if (panelElement) return panelElement.offsetLeft;
  return panel * (element?.clientWidth ?? 0);
}

function scrollToPanel(panel: number, behavior: ScrollBehavior = 'auto'): void {
  const element = scroller.value;
  const nextPanel = clampPanel(panel);

  if (!element) return;
  element.scrollTo({
    left: getPanelLeft(nextPanel),
    behavior,
  });
}

function syncCurrentPanel(): void {
  const element = scroller.value;
  const panelElements = getPanelElements();

  if (!element || panelElements.length === 0) {
    currentPanel.value = 0;
    return;
  }

  let closestPanel = 0;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const [panel, panelElement] of panelElements.entries()) {
    const distance = Math.abs(panelElement.offsetLeft - element.scrollLeft);

    if (distance < closestDistance) {
      closestPanel = panel;
      closestDistance = distance;
    }
  }

  currentPanel.value = clampPanel(closestPanel);
}

function resetScroll(): void {
  currentPanel.value = 0;
  void nextTick(() => scrollToPanel(0, 'auto'));
}

onMounted(resetScroll);

watch(() => props.panelCount, resetScroll);
</script>

<template>
  <div
    class="panel-scroller"
    :class="{ 'panel-scroller--paginated': panelCount > 1 }"
  >
    <div
      ref="scroller"
      class="panel-scroller__window"
      :aria-label="ariaLabel"
      :data-testid="testId"
      @scroll.passive="syncCurrentPanel"
    >
      <div class="panel-scroller__track">
        <slot panel-class="panel-scroller__panel" />
      </div>
    </div>

    <div
      v-if="panelCount > 1"
      class="panel-scroller__dots"
      data-testid="panel-pagination-dots"
    >
      <span
        v-for="panel in panelCount"
        :key="panel"
        class="panel-scroller__dot"
        :class="{ 'panel-scroller__dot--active': panel - 1 === currentPanel }"
        :data-testid="`panel-pagination-dot-${panel - 1}`"
      />
    </div>

  </div>
</template>

<style scoped>
.panel-scroller {
  height: 100%;
  --panel-gap: 128px;
}

.panel-scroller--paginated {
  display: grid;
  grid-template-rows: minmax(0, 1fr) 40px;
  row-gap: 2px;
}

.panel-scroller__window {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}

.panel-scroller__window::-webkit-scrollbar {
  display: none;
}

.panel-scroller__track {
  display: flex;
  width: 100%;
  height: 100%;
  gap: var(--panel-gap);
}

.panel-scroller__dots {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
}

.panel-scroller__dot {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
}

.panel-scroller__dot::before {
  width: 16px;
  height: 16px;
  content: '';
  background: color-mix(in srgb, var(--entr-text-muted) 58%, transparent);
  border-radius: 999px;
  transition:
    background 180ms var(--entr-ease),
    transform 180ms var(--entr-ease);
}

.panel-scroller__dot--active::before {
  background: var(--entr-text);
  transform: scale(1.125);
}
</style>
